import { Head, Link, router } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import PageHeader from '@/components/common/PageHeader'
import RemainingTimeDisplay from '@/components/reservations/RemainingTimeDisplay'
import PrescriptionNotice from '@/components/reservations/PrescriptionNotice'
import type { ProvisionalReservation, PatientFamilyProfile } from '@/types/schedule'

type Props = {
  provisional_reservation: ProvisionalReservation
  patient_family_profiles: PatientFamilyProfile[]
  selected_patient_family_profile: PatientFamilyProfile | null
}

// 仮予約情報カードコンポーネント
function ReservationSlotCard({
  reservation,
  onCancel,
}: {
  reservation: ProvisionalReservation
  onCancel: () => void
}) {
  return (
    <div className="oc-info-card mb-6">
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-base font-semibold text-[#2a3039]">仮予約中の診療枠</h3>
      </div>
      <div className="space-y-1 text-sm text-[#515862]">
        <p>
          <span className="text-[#6f7680]">診療科目: </span>
          {reservation.medical_department.name}
        </p>
        <p>
          <span className="text-[#6f7680]">クリニック: </span>
          {reservation.clinic.name}
        </p>
        <p>
          <span className="text-[#6f7680]">日時: </span>
          {reservation.doctor_schedule.formatted_date} {reservation.doctor_schedule.formatted_time}
        </p>
        <p>
          <span className="text-[#6f7680]">担当医師: </span>
          {reservation.doctor_schedule.doctor.full_name}
        </p>
        <PrescriptionNotice
          nightSlot={reservation.doctor_schedule.night_slot}
          expectedPrescriptionIssuedDate={reservation.doctor_schedule.expected_prescription_issued_date}
          pharmacyReceivableFrom={reservation.doctor_schedule.pharmacy_receivable_from}
          className="mt-1"
        />
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="mt-3 text-sm text-red-600 hover:underline"
      >
        予約枠を変更する
      </button>
    </div>
  )
}

// 患者選択アイテムコンポーネント
function PatientListItem({
  profile,
  isSelected,
  onSelect,
}: {
  profile: PatientFamilyProfile
  isSelected: boolean
  onSelect: (id: number) => void
}) {
  // すでに予約が入っている家族は選べない。理由を添えて、なぜ選べないのかが分かるようにする(#1060)
  const isUnreservable = profile.reservable === false

  return (
    <label
      className={`block rounded-lg border p-4 transition ${
        isUnreservable
          ? 'cursor-not-allowed border-gray-200 bg-gray-50'
          : isSelected
            ? 'cursor-pointer border-cyan-500 bg-cyan-50 shadow-[0_0_0_1px_rgba(33,150,243,0.15)]'
            : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="flex items-start">
        <input
          type="radio"
          name="patient_family_profile_id"
          value={profile.id}
          checked={isSelected}
          disabled={isUnreservable}
          onChange={() => onSelect(profile.id)}
          className="oc-radio mt-1 mr-3"
        />
        <div className="flex-1">
          <div className={`font-medium ${isUnreservable ? 'text-gray-400' : ''}`}>
            {profile.full_name}
          </div>
          <div className="text-sm text-gray-500">{profile.full_name_kana}</div>
          <div className="text-sm text-gray-500 mt-1">
            {profile.birthday} / {profile.sex}
          </div>
          {isUnreservable && (
            <div className="text-sm text-[#b26500] mt-1">
              {profile.unreservable_reason ?? 'すでに予約が入っています'}
            </div>
          )}
          {!isUnreservable && !profile.is_complete && (
            <div className="text-sm text-red-500 mt-1">
              プロフィールが未完成です
            </div>
          )}
        </div>
      </div>
    </label>
  )
}

// 選択中の患者詳細パネルコンポーネント
function PatientDetailPanel({
  profile,
  provisionalReservationToken,
}: {
  profile: PatientFamilyProfile
  provisionalReservationToken: string
}) {
  return (
    <div className="mt-6 rounded-lg border border-[#d8dde3] bg-[#eef3f7] p-4">
      <h3 className="mb-2 font-medium text-[#2a3039]">選択中の患者情報</h3>
      <dl className="text-sm space-y-1">
        <div className="flex">
          <dt className="w-24 text-gray-500">氏名</dt>
          <dd>{profile.full_name}</dd>
        </div>
        <div className="flex">
          <dt className="w-24 text-gray-500">フリガナ</dt>
          <dd>{profile.full_name_kana}</dd>
        </div>
        <div className="flex">
          <dt className="w-24 text-gray-500">生年月日</dt>
          <dd>{profile.birthday}</dd>
        </div>
        <div className="flex">
          <dt className="w-24 text-gray-500">性別</dt>
          <dd>{profile.sex}</dd>
        </div>
        {profile.phone_number && (
          <div className="flex">
            <dt className="w-24 text-gray-500">電話番号</dt>
            <dd>{profile.phone_number}</dd>
          </div>
        )}
        {profile.address && (
          <div className="flex">
            <dt className="w-24 text-gray-500">住所</dt>
            <dd>{profile.address}</dd>
          </div>
        )}
      </dl>
      <Link
        href={`/online_clinic/patient_family_profiles/${profile.id}/edit?provisional_reservation_token=${provisionalReservationToken}`}
        className="mt-2 inline-block text-sm text-[#2196f3] hover:underline"
      >
        プロフィールを確認・変更
      </Link>
    </div>
  )
}

// アクションボタンコンポーネント
function ActionButtons({
  canProceed,
  onCancel,
  onNext,
}: {
  canProceed: boolean
  onCancel: () => void
  onNext: () => void
}) {
  return (
    <div className="oc-action-bar">
      <div className="oc-action-container">
        <button
          type="button"
          onClick={onCancel}
          className="button-secondary"
        >
          キャンセル
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="button-primary"
        >
          次へ
        </button>
      </div>
    </div>
  )
}

export default function SelectPatient({
  provisional_reservation,
  patient_family_profiles,
  selected_patient_family_profile,
}: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(
    selected_patient_family_profile?.id ?? null
  )
  const [error, setError] = useState<string | null>(null)
  // 受診者変更時のリセット確認モーダル
  const [pendingProfileId, setPendingProfileId] = useState<number | null>(null)

  // propsが更新されたらstateも更新（戻る操作などでpropsが変わった場合に追従）
  useEffect(() => {
    setSelectedId(selected_patient_family_profile?.id ?? null)
  }, [selected_patient_family_profile?.id])

  const selectedProfile = patient_family_profiles.find((p) => p.id === selectedId)
  const canProceed = (selectedProfile?.is_complete ?? false) && selectedProfile?.reservable !== false

  const applyPatientChange = (profileId: number) => {
    setSelectedId(profileId)
    router.get(
      '/online_clinic/reservations/select_patient',
      {
        provisional_reservation_token: provisional_reservation.token,
        patient_family_profile_id: profileId,
      },
      { preserveState: true, preserveScroll: true }
    )
  }

  const handlePatientChange = (profileId: number) => {
    // 既に選択済みの受診者から変更する場合、入力内容リセットの確認を表示
    const savedProfileId = provisional_reservation.patient_family_profile_id
    if (
      savedProfileId &&
      savedProfileId !== profileId &&
      provisional_reservation.has_interview_data
    ) {
      setPendingProfileId(profileId)
      return
    }

    applyPatientChange(profileId)
  }

  const handleConfirmPatientChange = () => {
    if (pendingProfileId == null) return
    const profileId = pendingProfileId
    setPendingProfileId(null)
    applyPatientChange(profileId)
  }

  const handleCancelPatientChange = () => {
    setPendingProfileId(null)
  }

  const handleNext = () => {
    if (!selectedId || !canProceed) return
    setError(null)

    // 患者選択を仮予約に保存してから問診ステップ1へ遷移
    // サーバー側でリダイレクトを返すので、自動的に遷移する
    router.patch(
      `/online_clinic/provisional_reservations/${provisional_reservation.token}/select_patient`,
      { patient_family_profile_id: selectedId },
      {
        onError: () => {
          setError('エラーが発生しました')
        },
      }
    )
  }

  const handleCancel = () => {
    if (!confirm('仮予約をキャンセルしてもよろしいですか？')) return
    setError(null)

    // サーバー側でリダイレクトを返すので、自動的に遷移する
    router.delete(
      `/online_clinic/provisional_reservations/${provisional_reservation.token}`,
      {
        onError: () => {
          setError('キャンセルに失敗しました')
        },
      }
    )
  }

  const handleBack = () => {
    if (!confirm('予約枠の選択に戻りますか？\n現在の仮予約は解放されます。')) return
    setError(null)

    router.delete(
      `/online_clinic/provisional_reservations/${provisional_reservation.token}`,
      {
        onError: () => {
          setError('戻る操作に失敗しました')
        },
      }
    )
  }

  return (
    <>
      <Head title="患者選択 - オンライン診療予約" />

      <RemainingTimeDisplay expiresAt={provisional_reservation.expires_at} />

      {/* 固定バーの高さ分のスペーサー */}
      <div className="h-9" />

      <div className="min-h-screen bg-white">
        <PageHeader
          leading={
            <button
              type="button"
              onClick={handleBack}
              className="text-[#6f7680] hover:text-[#2a3039]"
              aria-label="前のページへ戻る"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          }
          subtitle="患者選択"
        />

        <main className="mx-auto max-w-3xl px-4 py-6">
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <ReservationSlotCard
            reservation={provisional_reservation}
            onCancel={handleCancel}
          />

          {/* 患者選択 */}
          <div className="oc-card">
            <h2 className="oc-section-title">どなたが診療を受けますか？</h2>

            <div className="space-y-4">
              {patient_family_profiles.map((profile) => (
                <PatientListItem
                  key={profile.id}
                  profile={profile}
                  isSelected={selectedId === profile.id}
                  onSelect={handlePatientChange}
                />
              ))}
            </div>

            {selectedProfile && (
              <PatientDetailPanel
                profile={selectedProfile}
                provisionalReservationToken={provisional_reservation.token}
              />
            )}

            <div className="mt-6">
              <Link
                href={`/online_clinic/patient_family_profiles/new?provisional_reservation_token=${provisional_reservation.token}`}
                className="oc-link"
              >
                + 家族を追加
              </Link>
            </div>
          </div>

          <ActionButtons
            canProceed={canProceed}
            onCancel={handleCancel}
            onNext={handleNext}
          />

          {/* ボタン分のスペース確保 */}
          <div className="h-24" />
        </main>
      </div>

      {/* 受診者変更時の確認モーダル */}
      {pendingProfileId != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">受診者を変更しますか？</h3>
            <p className="text-sm text-gray-600 mb-6">
              受診者を変更すると、入力済みの問診内容がリセットされます。よろしいですか？
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelPatientChange}
                data-testid="patient-change-cancel"
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleConfirmPatientChange}
                data-testid="patient-change-confirm"
                className="flex-1 py-2 px-4 bg-[#2196f3] text-white rounded-lg hover:bg-[#1976d2]"
              >
                変更する
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
